/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, atom } from 'jotai';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import {
  CompetitorGroupVehicle,
  competitorGroupVehicleByVehicleNumberAndSeriesIdSelectorFamily,
} from 'jotai/competitorGroup';
import { addOrUpdateEntityItem } from 'jotai/entity';
import { activeSeriesIdSelector } from 'jotai/session';
import { activeWorkspaceAtom } from 'jotai/workspace';
import { ReferenceVehicle } from './ReferenceVehicleObject';
import { useCallback } from 'react';

export const selectReferenceVehicleOpenAtom = atom<boolean>(false);

const selectedReferenceVehiclesAtomFamily = atomFamily<string, Atom<ReferenceVehicle>>((seriesId: string) =>
  atom<ReferenceVehicle>((get) => {
    const workspace = get(activeWorkspaceAtom);
    if (Object.keys(workspace).length > 0) {
      return (
        workspace.workspace.selectedReferenceVehicle?.byId[seriesId] || {
          seriesId: seriesId,
          selectedVehicle: '',
        }
      );
    } else {
      return {
        seriesId: seriesId,
        selectedVehicle: '',
      };
    }
  })
);

//This doesn't seem to be used even in the recoil file other than to be set but not used?
export const selectedReferenceVehicleByActiveSeriesIdAtom = atom<string | undefined>((get) => {
  const activeSeriesId = get(activeSeriesIdSelector);
  if (activeSeriesId) {
    const workspace = get(activeWorkspaceAtom);
    if (Object.keys(workspace).length > 0) {
      return workspace.workspace.selectedReferenceVehicle?.byId[activeSeriesId].selectedVehicle || '';
    }
  } else {
    return '';
  }
});

// use this selector to get the selected reference vehicle by series
export const selectedReferenceVehiclesColorSelectorFamily = atomFamily<
  string,
  Atom<CompetitorGroupVehicle | undefined>
>((seriesId: string) =>
  atom<CompetitorGroupVehicle | undefined>((get) => {
    const selectedReferenceVehicle = get(selectedReferenceVehiclesAtomFamily(seriesId));
    if (selectedReferenceVehicle.selectedVehicle !== '') {
      const competitorGroup = get(
        competitorGroupVehicleByVehicleNumberAndSeriesIdSelectorFamily({
          seriesId: seriesId,
          vehicleNumber: selectedReferenceVehicle.selectedVehicle,
        })
      );
      return competitorGroup;
    }
  })
);

// // use this selector to get the active selected reference vehicle by series
export const selectedReferenceVehicleByActiveSeriesIdColorSelectorFamily = atom<CompetitorGroupVehicle | undefined>(
  (get) => {
    const activeSeriesId = get(activeSeriesIdSelector);
    if (activeSeriesId) {
      const selectedReferenceVehicle = get(selectedReferenceVehiclesAtomFamily(activeSeriesId));
      if (selectedReferenceVehicle.selectedVehicle !== '') {
        const competitorGroup = get(
          competitorGroupVehicleByVehicleNumberAndSeriesIdSelectorFamily({
            seriesId: activeSeriesId,
            vehicleNumber: selectedReferenceVehicle.selectedVehicle,
          })
        );
        return competitorGroup;
      }
    }
  }
);

// // use this callback to update the selected reference vehicle by series
export const useUpdateSelectedReferenceVehicle = () => {
  return useAtomCallback(
    useCallback((get, set, referenceVehicle: ReferenceVehicle) => {
      const currentWorkspace = get(activeWorkspaceAtom);
      addOrUpdateEntityItem(
        currentWorkspace.workspace.selectedReferenceVehicle,
        referenceVehicle,
        referenceVehicle.seriesId
      );
      set(activeWorkspaceAtom, { ...currentWorkspace });
    }, [])
  );
};
